/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ComponentType } from 'react';

import { useLivePreviewEntry } from '../hooks/useLivePreviewEntry';
import { LIVE_PREVIEW_ENABLED } from '../setup/settings';
import { PlainObject } from '../helpers/helpers';
import PreviewErrorBoundary from './PreviewErrorBoundary';

export interface WithLivePreviewOptions {
  dataKey: string;
  contentTypeBase: string;
}

const getDisplayName = (Component: ComponentType<any>): string =>
  Component.displayName || Component.name || 'Component';

export const withLivePreview = <TProps extends { data: any }>(
  PageTemplate: ComponentType<TProps>,
  { dataKey, contentTypeBase }: WithLivePreviewOptions
): ComponentType<TProps> => {
  const WithLivePreview = (props: TProps) => {
    const queryData = props.data as PlainObject;
    const pageData = useLivePreviewEntry(queryData[dataKey], contentTypeBase);

    const page = (
      <PageTemplate {...props} data={{ ...queryData, [dataKey]: pageData }} />
    );

    return LIVE_PREVIEW_ENABLED ? (
      <PreviewErrorBoundary
        name="page"
        data={pageData}
        message={
          <>
            <strong>This page cannot be previewed yet</strong> - it is missing
            content it needs to render. Check the fields outside the page body.
          </>
        }
      >
        {page}
      </PreviewErrorBoundary>
    ) : (
      page
    );
  };

  WithLivePreview.displayName = `withLivePreview(${getDisplayName(PageTemplate)})`;

  return WithLivePreview as ComponentType<TProps>;
};
